import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import { useNavigate, Link } from "react-router-dom";
import { getRepresentativeWord } from "./robrosModule";
import CerrarSide from '../icons/arrowLeft';
import { FaCaretDown, FaSignOutAlt } from 'react-icons/fa';
import gql from 'graphql-tag';
import JobItem from './JobItem';
import ToolTipBarra from '../icons/TooltipBarra';
import './empleo.css';

// Nueva query que utiliza el resolver "busquedaLaboral"
const JOBS_QUERY = gql`
  query ($argumento: String!) {
    busquedaLaboral(argumento: $argumento) {
      id
      rubro
      puesto
      habilidadesBlandas
      conocimientosDuros
      vector
      additionalFields
      score
    }
  }
`;

// Query para traer todos los trabajos activos (se usa para el historial)
const JOB_ACTIVES_QUERY = gql`
  query JobActives {
    jobActives {
      id
      puesto
    }
  }
`;

const PantallaPrincipal = () => {
  // Estados para el sidebar y dropdown
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const handleDropdownToggle = () => setShowDropdown(!showDropdown);
  const id = null; // Identificador del trabajo activo (según corresponda)
  const navigate = useNavigate();
  const { loginWithRedirect, logout, user, isAuthenticated, isLoading } = useAuth0();

  const useradmin = [
    process.env.REACT_APP_ADMIN,
    process.env.REACT_APP_ADMIN2,
    process.env.REACT_APP_ADMIN3,
    process.env.REACT_APP_ADMIN4,
    process.env.REACT_APP_ADMIN5,
    process.env.REACT_APP_ADMIN6,
  ];

  const isAdmin = (currentUser) => {
    return useradmin.includes(currentUser);
  };

  const { loading: loadingJobs, error: errorJobs, data: dataJobs } = useQuery(JOB_ACTIVES_QUERY);

  const handleChatNavigation = () => {
    localStorage.clear();
    navigate(`/new/chat`);
  };

  // Cierra el dropdown si se hace click fuera de él
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <>
      {isAuthenticated && (
        <img
          src="/images/LOGO-conductualGPT-01.svg"
          className={`floating-menu-logo-silgleJob ${!sidebarOpen && 'collapsed'}`}
        />
      )}
      {isAuthenticated && (
        <div className="profile-container">
          {/* Menú desplegable y logo del header */}
          <div id="dropdown" ref={dropdownRef} className="dropdown">
            <img src={user.picture} alt={user.name} onClick={handleDropdownToggle} />
            {showDropdown && (
              <div className="dropdown-menu">
                <button>
                  <a className="button-link" href="#" onClick={handleChatNavigation}>
                    Inicio
                  </a>
                </button>
                <button>
                  <a className="button-link" href="#" onClick={() => navigate(`/userlinkOwner`)}>
                    Gestionar Links
                  </a>
                </button>
                <button>
                  <a className="button-link" href="#" onClick={() => navigate(`/equipos`)}>
                    Crear equipo
                  </a>
                </button>
                <button>
                  <a className="button-link" href="#" onClick={() => navigate(`/createJob`)}>
                    Puesto laboral
                  </a>
                </button>
                <button
                  onClick={() => {
                    localStorage.clear();
                    logout({ logoutParams: { returnTo: window.location.origin } });
                  }}
                >
                  <span>Salir</span>
                  <FaSignOutAlt />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="contenedor">
        {/* Sidebar integrado */}
        <div>
          <ToolTipBarra
            sidebarOpen={sidebarOpen}
            content={sidebarOpen ? 'Cerrar barra lateral' : 'Abrir barra lateral'}
          >
            <button
              className={`sidebar-toggle ${!sidebarOpen ? 'collapsed' : ''}`}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                position: 'fixed',
                left: sidebarOpen ? '206px' : '20px',
                top: '6px',
                zIndex: 1000,
                transition: 'all 0.3s ease',
                background: '#fff',
                border: '0px solid #ddd',
                borderRadius: '50%',
                width: '33px',
                height: '33px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              {sidebarOpen ? '✕' : '☰'}
            </button>
          </ToolTipBarra>
          <div className={`sidebar ${!sidebarOpen ? 'closed' : ''}`}>
            <div className="closeButtonContainer"></div>
            {/* Historial de trabajos */}
            <div className="jobHistory">
              <h3>Vacantes publicadas</h3>
              {loadingJobs && <p>Cargando vacantes...</p>}
              {errorJobs && <p>Error al cargar vacantes</p>}
              {dataJobs && dataJobs.jobActives && dataJobs.jobActives.length > 0 ? (
                <ul>
                  {dataJobs.jobActives.map((jobItem) => (
                    <li
                      key={jobItem.id}
                      className={`jobHistoryItem ${id === jobItem.id ? 'activeJob' : ''}`}
                    >
                      <Link to={`/job/${jobItem.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        {jobItem.puesto}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No hay historial de trabajos.</p>
              )}
            </div>
          </div>
        </div>
        {/* Componente de búsqueda */}
        <SearchBar />
      </div>
    </>
  );
};

const SearchBar = () => {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState([]);
  const [selectedRubro, setSelectedRubro] = useState('');

  // Usamos la nueva query que invoca busquedaLaboral
  const [getJobs, { loading, error }] = useLazyQuery(JOBS_QUERY, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      // data.busquedaLaboral es directamente el array de objetos de tipo Job
      setResults(data.busquedaLaboral);
      setSelectedRubro('');
    },
  });

  const handleChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleClear = () => {
    setSearchText('');
    setResults([]);
    setSelectedRubro('');
  };

  const handleSearch = () => {
    if (searchText.trim() !== '') {
      getJobs({ variables: { argumento: searchText } });
    }
  };

  const repRubros = [...new Set(results.map((job) => getRepresentativeWord(job.rubro)))];

  const handleSelectRubro = (rep) => {
    setSelectedRubro(rep);
  };

  const filteredResults = selectedRubro
    ? results.filter((job) => getRepresentativeWord(job.rubro) === selectedRubro)
    : results;

  const containerClass = results.length > 0 ? 'searchContainer fixedSearchContainer' : 'searchContainer';

  return (
    <div className={containerClass}>
      <div className="searchBarContainer">
        <input
          type="text"
          placeholder="Busca y selecciona un rol, edita los requisitos y Conductual-GPT buscará a los mejores postulantes"
          value={searchText}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          className="input"
        />
        {searchText && (
          <button className="clearButton" onClick={handleClear}>
            ✕
          </button>
        )}
        <div className="iconGroup">
          <button className="iconButton" onClick={handleSearch}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="steelblue"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
        </div>
      </div>
      {repRubros.length > 0 && (
        <div className="categoryLinksContainer">
          {repRubros.map((rep) => (
            <button
              key={rep}
              className={rep === selectedRubro ? 'categoryLink categoryLinkActive' : 'categoryLink'}
              onClick={() => handleSelectRubro(rep)}
            >
              {rep}
            </button>
          ))}
        </div>
      )}
      <div className="resultsContainer">
        {loading && <p>Buscando con Conductual-GPT...</p>}
        {error && <p>Error: {error.message}</p>}
        {filteredResults.map((job) => (
          <JobItem key={job.id} job={job} />
        ))}
      </div>
    </div>
  );
};

export default PantallaPrincipal;
